/**
 * Recipe showcase — control primitives (Button, Badge, Card, Inputs).
 *
 * Enumerates every variant × size × state of each CVA recipe so the Tahoe
 * restyle can be judged in the real token environment. Imports ONLY from the
 * shared UI barrel and @/lib/cn (workshop rules). Dummy data + local state only.
 */

import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  TextInput,
  PasswordInput,
  Icons,
} from "@/components/ui";
import { Section, Row, Label } from "./recipes-primitives";

/* ─── Button ──────────────────────────────────────────────────────────────── */

const BUTTON_VARIANTS = [
  "default",
  "secondary",
  "ghost",
  "destructive",
  "link",
] as const;

export function ButtonSection() {
  return (
    <Section
      name="button"
      title="Button"
      caption="capsule · h36 · pill radius · body text · heavy glass + stroke (secondary)"
    >
      <div className="space-y-5">
        <div>
          <Label>default size · all variants</Label>
          <Row>
            {BUTTON_VARIANTS.map((variant) => (
              <Button
                key={variant}
                variant={variant}
                data-testid={`recipe-button-${variant}`}
              >
                {variant}
              </Button>
            ))}
          </Row>
        </div>

        <div>
          <Label>sm size</Label>
          <Row>
            {BUTTON_VARIANTS.filter((v) => v !== "link").map((variant) => (
              <Button
                key={variant}
                variant={variant}
                size="sm"
                data-testid={`recipe-button-sm-${variant}`}
              >
                {variant}
              </Button>
            ))}
          </Row>
        </div>

        <div>
          <Label>lg size · tab size</Label>
          <Row>
            <Button variant="default" size="lg" data-testid="recipe-button-lg">
              Continue
            </Button>
            <Button variant="secondary" size="lg">
              Cancel
            </Button>
            <Button variant="ghost" size="tab" data-testid="recipe-button-tab">
              Tab item
            </Button>
            <Button variant="secondary" size="tab">
              Tab item
            </Button>
          </Row>
        </div>

        <div>
          <Label>icon sizes</Label>
          <Row>
            <Button variant="default" size="icon" aria-label="Add" data-testid="recipe-button-icon">
              <Icons.Plus className="h-4 w-4" />
            </Button>
            <Button variant="secondary" size="icon" aria-label="Search">
              <Icons.Search className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" aria-label="Settings">
              <Icons.Settings className="h-4 w-4" />
            </Button>
            <Button variant="secondary" size="iconSm" aria-label="Edit" data-testid="recipe-button-iconsm">
              <Icons.Edit className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="iconSm" aria-label="More">
              <Icons.MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </Row>
        </div>

        <div>
          <Label>disabled</Label>
          <Row>
            <Button variant="default" disabled>
              default
            </Button>
            <Button variant="secondary" disabled>
              secondary
            </Button>
            <Button variant="destructive" disabled>
              destructive
            </Button>
            <Button variant="ghost" size="icon" disabled aria-label="Disabled">
              <Icons.Trash2 className="h-4 w-4" />
            </Button>
          </Row>
        </div>
      </div>
    </Section>
  );
}

/* ─── Badge ───────────────────────────────────────────────────────────────── */

const BADGE_VARIANTS = [
  "default",
  "error",
  "warning",
  "selected",
  "outline",
  "status-success",
  "status-error",
] as const;

export function BadgeSection() {
  return (
    <Section
      name="badge"
      title="Badge"
      caption="pill · h22 · mini text · tint fill + stroke · 6px status dot"
    >
      <Row>
        {BADGE_VARIANTS.map((variant) => (
          <Badge
            key={variant}
            variant={variant}
            data-testid={`recipe-badge-${variant}`}
          >
            {variant}
          </Badge>
        ))}
      </Row>
    </Section>
  );
}

/* ─── Card ────────────────────────────────────────────────────────────────── */

const CARD_VARIANTS = ["default", "frame", "panel", "modal"] as const;

export function CardSection() {
  return (
    <Section
      name="card"
      title="Card"
      caption="surface roles · frame / panel / modal glass · appkit spacing + typography"
    >
      <div className="flex flex-wrap gap-4">
        {CARD_VARIANTS.map((variant) => (
          <Card
            key={variant}
            variant={variant}
            className="w-64"
            data-testid={`recipe-card-${variant}`}
          >
            <CardHeader>
              <CardTitle>{variant} card</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>
                A short description showing the {variant} surface fill, stroke
                and blur in the current token environment.
              </CardDescription>
            </CardContent>
            <CardFooter>
              <Button variant="secondary" size="sm">
                Action
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    </Section>
  );
}

/* ─── Inputs ──────────────────────────────────────────────────────────────── */

export function InputSection() {
  const [value, setValue] = useState("filled value");
  const [password, setPassword] = useState("hunter2");

  return (
    <Section
      name="input"
      title="TextInput / PasswordInput"
      caption="capsule · h36 · pill radius · body text · input glass + stroke · accent focus glow"
    >
      <div className="flex max-w-sm flex-col gap-4">
        <div>
          <Label>field shape (forms / login — default)</Label>
          <TextInput
            placeholder="you@example.com"
            data-testid="recipe-input-rest"
          />
        </div>
        <div>
          <Label>capsule shape (search / input bar)</Label>
          <TextInput
            shape="capsule"
            placeholder="Search frames…"
            data-testid="recipe-input-capsule"
          />
        </div>
        <div>
          <Label>filled</Label>
          <TextInput
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="recipe-input-filled"
          />
        </div>
        <div>
          <Label>error</Label>
          {/* TextInput has no error prop — error state is conveyed via className
              (red border/glow). This mirrors how FormField wires error styling. */}
          <TextInput
            defaultValue="invalid@"
            className="border-danger focus:border-danger focus:shadow-none"
            aria-invalid="true"
            data-testid="recipe-input-error"
          />
          <span className="mt-1 block text-appkit-mini text-danger">
            Enter a valid email address
          </span>
        </div>
        <div>
          <Label>disabled</Label>
          <TextInput
            value="locked value"
            readOnly
            disabled
            data-testid="recipe-input-disabled"
          />
        </div>
        <div>
          <Label>password (toggle visibility)</Label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="recipe-password"
          />
        </div>
      </div>
    </Section>
  );
}
